/**
 * This file mainly handles two things:
 * Overriding initial Stamped styles coming from Stamped's widget.
 * Re-applying styles and adjustments when dynamic changes are made (filtering, loading new reviews, submitting a review)
 */

/**
 * Stamped's boilerplate to prepare listeners.
 * @param {HTMLElement} el - The element to attach the event to.
 * @param {string} eventName - The event type to listen for.
 * @param {Function} handler - The handler to call when the event occurs.
 */
const addEventListenerStamped = (el, eventName, handler) => {
  if (el.addEventListener) {
    el.addEventListener(eventName, handler);
  } else {
    el.attachEvent(`on${eventName}`, () => {
      handler.call(el);
    });
  }
};

/**
 * Sets properties on a DOM element.
 *
 * @param {Element} element - The DOM element to modify.
 * @param {Object} properties - Properties to set on the element.
 */
function setElementProperties(element, properties) {
  Object.assign(element, properties);
}

/**
 * Applies styles to a DOM element.
 *
 * @param {Element} element - The DOM element to style.
 * @param {Object} styles - CSS styles to apply.
 */
function setElementStyle(element, styles) {
  Object.assign(element.style, styles);
}

/**
 * Handles moving the write a review section out of the header and below the review tabs.
 * @param {Element} messageContainer - The parent container for Stamped messages which holds reviews.
 * @param {Element} headerContainer - The header container which will need to be hidden.
 * @param {Element} writeAReviewContainer - The parent container with the review button, stars and text.
 * @param {Element} writeAReviewButton - The clickable CTA customers can use to write a review.
 * @param {Element} writeAReviewText - The text that prompts customers to leave a first review.
 * @param {Element} reviewForm - The review form that holds fields for customer input.
 */
function moveAndStyleWriteAReviewSection(
  messageContainer,
  headerContainer,
  writeAReviewContainer,
  writeAReviewText,
  reviewForm,
) {
  messageContainer.insertAdjacentElement("beforeend", writeAReviewContainer);
  messageContainer.classList.add("flex", "flex-col", "items-center");
  writeAReviewContainer.classList.add("m-auto", "flex", "p-4", "text-center");

  setElementProperties(writeAReviewText, {
    innerText: "Write the first review!",
  });

  setElementStyle(headerContainer, {
    display: "none",
  });

  messageContainer.insertAdjacentElement("beforeend", reviewForm);
}

/**
 * Add average rating text to the summary overview.
 * @param {Element} overview - The summary overview element.
 */
function addAverageRatingText(overview) {
  const averageRating = document.createElement("p");
  averageRating.className = "uppercase m-0 text-left px-4";
  averageRating.textContent = "Average rating";
  overview.insertAdjacentElement("beforeend", averageRating);
}

/**
 * Updates the display and layout of the summary in the reviews section.
 */
function updateSummary() {
  const stampedHeader = document.querySelector(".stamped-header");
  const sectionTitle = document.getElementById("reviews-container-title");
  const leftHeader = document.querySelector(".stamped-header-left");
  const rightHeader = document.querySelector(".stamped-header-right");
  const overview = document.querySelector(".summary-overview");
  const totalReviewsDisplay = document.querySelector(
    ".stamped-summary .stamped-summary-text",
  );
  const summary = document.querySelector(".stamped-summary");
  const writeAReviewContainer = document.querySelector(
    ".stamped-summary-actions",
  );
  const writeAReviewButton = document.querySelector(
    ".stamped-summary-actions-newreview",
  );
  const beTheFirstToWriteAReview = document.querySelector(
    ".stamped-empty-state div",
  );
  const reviewForm = document.querySelector(".new-review-form");
  const stampedMessages = document.querySelector(".stamped-messages");
  const reviews = parseInt(totalReviewsDisplay.dataset.count, 10);

  if (reviews > 0) sectionTitle.innerText = "See What Others Had to Say";
  addAverageRatingText(overview);

  totalReviewsDisplay.innerHTML = `
      <div class="total-reviews flex flex-col items-start">
          <span class="subheading font-semibold m-0">${reviews}</span>
          <p class="uppercase m-0">Total reviews</p>
      </div>
  `;
  leftHeader.appendChild(totalReviewsDisplay);
  stampedHeader.insertAdjacentElement("beforeend", rightHeader);

  if (reviews === 0) {
    moveAndStyleWriteAReviewSection(
      stampedMessages,
      stampedHeader,
      writeAReviewContainer,
      writeAReviewButton,
      beTheFirstToWriteAReview,
      reviewForm,
    );
  } else {
    summary.insertAdjacentElement("beforeend", writeAReviewContainer);
    leftHeader.appendChild(writeAReviewContainer);
  }
  leftHeader.classList.add("justify-between");
  totalReviewsDisplay.style.display = "flex";
  totalReviewsDisplay.style.justifyContent = "center";
}

/**
 * Updates the styles and visibility of the carousel and its components based on the presence of photos.
 */
function updateCarousel() {
  const rightHeader = document.querySelector(".stamped-header-right");
  const carouselPhotoCount = document.querySelectorAll(
    ".stamped-photos-carousel .stamped-photo",
  ).length;
  const carousel = document.querySelector(".stamped-photos-carousel div");

  carousel.classList.add("flex", "w-full", "inner-carousel");
  carousel.style.overflowX = "scroll";

  // Hide right header if no photos exist
  if (carouselPhotoCount === 0) {
    rightHeader.style.display = "none";
  }
}

/**
 * Updates the resolution of photo backgrounds in a photo carousel to a higher resolution if they are set to a lower resolution.
 */
function updatePhotoResolution() {
  const photos = document.querySelectorAll(
    ".stamped-photos-carousel .stamped-photo",
  );
  const lowRes = "tr=h-180";
  const hiRes = "tr=h-900";

  photos.forEach((photo) => {
    const currentBackground = photo.style.background;
    if (currentBackground.includes(lowRes)) {
      const newBackground = currentBackground.replace(lowRes, hiRes);
      setElementStyle(photo, { background: newBackground });
    }
  });
}

/**
 * Converts radio button questions in a form to dropdown selection.
 */
function convertRadioIntoDropdown(question, selector, children) {
  question.classList.add("stamped-dropdown");
  for (let i = 1; i < children.length; i += 1) {
    const child = children[i];
    const option = document.createElement("option");
    const inputValue = child.querySelector("input").value;

    child.classList.add(`answer${i}`);
    child.style.display = "none";
    option.innerText = inputValue;
    option.value = inputValue;
    selector.appendChild(option);
  }
  question.appendChild(selector);
  selector.addEventListener("change", () => {
    for (let i = 1; i < children.length; i += 1) {
      const child = children[i];
      const input = child.querySelector("input");
      const inputValue = input.value;

      if (inputValue === selector.value) {
        input.checked = true;
      } else input.checked = false;
    }
  });
}

/**
 * Updates form questions by checking if it can be converted to a dropdown.
 */
function updateFormQuestions() {
  const formQuestions = document.querySelectorAll(
    ".stamped-form-custom-question",
  );
  formQuestions.forEach((question) => {
    const questionText = question.firstChild.innerText;
    if (question.dataset.type === "radio") {
      const selector = document.createElement("select");
      const children = question.querySelectorAll("label");
      const selectFromDropdown = document.createElement("option");

      question.classList.add("stamped-question-grid");
      question.firstChild.classList.add("question");

      selectFromDropdown.innerText = "SELECT FROM DROPDOWN";
      selector.append(selectFromDropdown);

      if (questionText !== "How did the item fit?") {
        convertRadioIntoDropdown(question, selector, children);
      }
    }
  });
}

/**
 * Updates text and placeholders in the form fields of a review form.
 */
function updateFormFieldText() {
  // Select various parts of the form to modify
  const form = document.querySelector(".stamped-content .new-review-form");
  const rating = document.querySelector(".stamped-form-review-rating");
  const headline = document.querySelector(".stamped-form-review-title");
  const body = document.querySelector(".stamped-form-review-body");

  // Update styles and text content for general form fields
  form.style.border = "none";
  rating.querySelector("label").innerText = "Rate this Product";
  headline.querySelector("label").innerText = "Headline";
  headline.querySelector("input").placeholder = "Sum up your review";
  body.querySelector("label").innerText = "Write Your Review";
  body.querySelector(".stamped-form-input textarea").placeholder =
    "Give us the deets!";

  // Modify text labels for custom questions
  document
    .querySelectorAll(".stamped-form-custom-question")
    .forEach((question) => {
      const label = question.querySelector("label");
      const labelText = label.innerText;

      switch (labelText) {
        case "What size did you wear?":
          label.innerText = "What size did you buy?";
          break;
        case "Which of the following most closely matches how you would describe your body?":
          label.innerText =
            "Which of the following most closely resembles your body type?";
          break;
        case "How did the item fit?":
          label.innerText = "How did this item fit?";
          break;
        default:
          break;
      }
    });
}

/**
 * Updates form labels for optional fields.
 */
function updateOptionalFieldText() {
  const formFields = document.querySelectorAll(
    ".stamped-form-custom-question label",
  );

  formFields.forEach((field) => {
    // Check if the label's content matches "Weight (lbs)" or "Bust Size".
    if (
      field.textContent === "Weight (lbs)" ||
      field.textContent === "Bust Size"
    ) {
      const breakTag = field.querySelector("br");

      const optionalSpan = document.createElement("span");
      optionalSpan.textContent = " OPTIONAL";
      optionalSpan.style.fontWeight = "400";

      breakTag.insertAdjacentElement("beforebegin", optionalSpan);
    }
  });
}

// TODO: Remove once scripts.js can use modules
const stampedToggleTab = (e) => {
  const heading = e.currentTarget;
  const container = heading.nextElementSibling;
  if (container.classList.contains("open-tab")) {
    container.classList.remove("open-tab");
    container.classList.remove("visible");
    heading.querySelector(".tab-icon").innerText = `+`;
    container.setAttribute("aria-hidden", "true");
  } else {
    container.classList.add("open-tab");
    container.classList.add("visible");
    heading.querySelector(".tab-icon").innerText = `–`;
    container.setAttribute("aria-hidden", "false");
  }
};

/**
 * Creates and appends custom review guidelines to the review form.
 */
function createReviewGuidelines() {
  const rating = document.querySelector(".stamped-form-review-rating");
  const tabContainer = document.createElement("div");

  const reviewGuidelines = `
      <div tabindex="0" role="button" aria-expanded="false" class="font-semibold tab-heading flex cursor-pointer items-center justify-between">
          <div class="h4 tab-heading-text text-base" role="heading">Review Guidelines</div>
          <div class="tab-icon">+</div>
      </div>
      <div class="tab-body">
          <b>Tips for Writing a Helpful Review</b>
          <p>1. A picture’s worth 1000 words. Photos are great! Just make sure that you <em>don’t include</em> any images of minors in your upload, since the pictures are published on the web. Pro wedding pics, selfies, and anything in between can help tell a complete story for brides and bridesmaids.</p>
          <p>2. Didn’t love it? Tell us why and be descriptive (not destructive), so we can do something about it. While writing your review, keep in mind that we really do read every review and do our best to make adjustments accordingly.</p>
          <p>3. We also love compliments, so if you loved your items, let us know.</p>
          <p>4. The more context the better! We’ve included a handful of optional questions on our review form that can serve as valuable metrics to customers with similar body types and dress sizes.</p>
      </div>
  `;

  // Set the class for the container and append the guidelines content
  tabContainer.className = "stamped-form-review-guidelines tab-container";
  tabContainer.innerHTML = reviewGuidelines;
  rating.after(tabContainer);

  // Attach event listeners for interaction with the guidelines tab
  tabContainer.querySelectorAll(".tab-heading").forEach((heading) => {
    heading.addEventListener("click", stampedToggleTab);
    heading.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        stampedToggleTab(event);
      }
    });
  });
}

/**
 * Styles the "Add Photos or Videos" button in the form.
 */
function styleAddPhotosButton() {
  const btn = document.querySelector(".stamped-file-uploader-btn");

  btn.innerHTML = `
    <p class="text-left mt-0"><span class="font-semibold">Add photos or videos</span> Optional</p>
    <p>Pictures are super helpful for other shoppers!</p>
    <div style="font-size: 86px; max-width: 400px; background-color: var(--deepBeige-25); border: 1px solid var(--darkerMocha); height: 150px; text-align: center; display: flex; justify-content: center; align-items: center;">+</div>
  `;

  btn.style.cssText = `
      display: block;
      text-align: left;
      margin: 0;
      padding-top: 0;
      color: #464646;
      border: 0;
      border-radius: 4px !important;
      max-width: 400px;
  `;
}

/**
 * Ensures date format matches: MONTH DAY, YEAR
 *
 * @param {Element} dateElement - The DOM element containing the date text to be updated.
 */
function formatDate(dateElement) {
  let dateText = dateElement.innerText;
  if (!dateText.includes(",")) {
    const indexOfLastWhiteSpace = dateText.lastIndexOf(" ");
    dateText = `${dateText.substring(0, indexOfLastWhiteSpace)}, ${dateText.substring(indexOfLastWhiteSpace + 1)}`;
    setElementProperties(dateElement, { innerText: dateText });
  }
}

/**
 * Adjusts the format of the date and moves the date element to after the reviewer's name.
 */
function updateDateOfReview() {
  const reviews = document.querySelectorAll(".stamped-review");

  reviews.forEach((review) => {
    const name = review.querySelector(".author");
    const date = review.querySelector(".created");
    const originalDateText = date.innerText;

    // Reformat date
    if (originalDateText.includes("/")) {
      const dateParts = originalDateText.split("/");
      const formattedDate = new Date(
        dateParts[2],
        dateParts[0] - 1,
        dateParts[1],
      );
      const displayDate = formattedDate
        .toDateString()
        .split(" ")
        .slice(1)
        .join(" ");

      date.innerText = displayDate;
      name.after(date);
    }

    formatDate(date);
  });
}

/**
 * Updates the labels of review attributes (Stamped's "review options") to match desired text.
 */
function updateReviewAttributes() {
  const reviews = document.querySelectorAll(".stamped-review");

  reviews.forEach((review) => {
    const reviewOptionsContainer = review.querySelector(
      ".stamped-review-options",
    );
    const reviewOptions = review.querySelectorAll(".stamped-review-options li");
    const reviewProduct = review.querySelector(".stamped-review-product");

    reviewOptions.forEach((option) => {
      const optionValue = option.dataset.value;
      // strongElement represents the label of each review option
      const strongElement = option.querySelector("strong");
      // Determine what to text to swap in
      switch (optionValue) {
        case "how-did-the-item-fit":
        case "overall-fit":
          setElementProperties(strongElement, { innerText: "OVERALL FIT:" });
          break;
        case "which-of-the-following-most-closely-matches-how-you-would-describe-your-body":
        case "body-type":
          setElementProperties(strongElement, { innerText: "BODY TYPE:" });
          break;
        case "what-size-did-you-wear":
        case "size-worn":
          setElementProperties(strongElement, { innerText: "SIZE WORN:" });
          break;
        case "weight":
        case "weight-lbs":
          setElementProperties(strongElement, { innerText: "WEIGHT (LBS):" });
          break;
        case "height":
          setElementProperties(strongElement, { innerText: "HEIGHT:" });
          break;
        case "bust-size":
        case "bust":
          setElementProperties(strongElement, { innerText: "BUST:" });
          break;
        default:
          break;
      }
    });

    reviewProduct.before(reviewOptionsContainer);
  });
}

/**
 * Updates filter elements, hiding certain filters and styling others.
 */
function updateFilters() {
  const filters = document.querySelectorAll(".stamped-filter-select");
  const filterLabel = document.querySelector(".stamped-reviews-filter-label");
  const filtersWrapper = document.querySelector(".stamped-filters-wrapper");
  const sortBy = document.querySelector(".stamped-sort-select-wrapper");
  const sortBy2 = document.querySelector(".stamped-sort-select2-wrapper");

  // Hide typical street wear size filter
  filters.forEach((filter) => {
    const firstOption = filter.querySelector("option");
    if (firstOption.innerText === "What's your typical street-wear size?") {
      setElementStyle(filter, { display: "none" });
    }
  });

  // Update the filter label and adjust widths and positions of sorting elements
  filterLabel.innerText = "FILTER";
  filterLabel.classList.add("text-base");
  sortBy.classList.add("w-full", "md:w-auto");
  sortBy2.classList.add("w-full", "md:w-auto");
  filtersWrapper.append(sortBy);
}

/**
 * Updates filter select elements to match desired text.
 * Creates clone so original HTML element is not manipulated.
 * Interacting with cloned elements trigger original element to update.
 */
function updateFilterSelectText() {
  const selectsContainer = document.querySelector(".stamped-filter-selects");
  const selects = document.querySelectorAll(".stamped-filter-selects select");
  const sortBy = document.querySelector(".stamped-sort-select2-wrapper");

  // Clone select elements
  selects.forEach((select) => {
    if (select.querySelector("option").innerText !== "Sort") {
      const clonedSelector = select.cloneNode(true);
      select.classList.add("stamped-selector");
      clonedSelector.classList.add("cloned-selector");
      selectsContainer.insertAdjacentElement("beforeend", clonedSelector);
    }
  });

  const stampedSelectors = document.querySelectorAll(
    ".stamped-filter-selects .stamped-selector",
  );
  const clonedSelectors = document.querySelectorAll(
    ".stamped-filter-selects .cloned-selector",
  );
  const lastclonedSelector = document.querySelector(
    ".cloned-selector:last-child",
  );
  lastclonedSelector?.after(sortBy);

  // Update select options
  clonedSelectors.forEach((selector) => {
    const firstOption = selector.querySelector("option");
    switch (firstOption.innerText) {
      case "What size did you wear?":
        firstOption.innerText = "Size Worn";
        break;
      case "Which of the following most closely matches how you would describe your body?":
        firstOption.innerText = "Body Type";
        break;
      default:
        break;
    }

    selector.addEventListener("change", (e) => {
      const selectorText = firstOption.innerText;
      let textToMatch = "";

      switch (selectorText) {
        case "How did the item fit?":
          textToMatch = "How did the item fit?";
          break;
        case "Size Worn":
          textToMatch = "What size did you wear?";
          break;
        case "Body Type":
          textToMatch =
            "Which of the following most closely matches how you would describe your body?";
          break;
        default:
          break;
      }

      // Usage within your forEach loop
      stampedSelectors.forEach((stampedSelector) => {
        const optionElement = stampedSelector.querySelector("option");
        if (optionElement && optionElement.innerText === textToMatch) {
          setElementProperties(stampedSelector, { value: selector.value });
          setElementProperties(stampedSelector, {
            selectedIndex: selector.selectedIndex,
          });
          window.StampedFn.filterReviews(e);
        }
      });
    });
  });
}

/**
 * Checks if updates are needed to any elements that were re-written due to dynamic changes
 */
function checkIfUpdatesNeeded() {
  const targetNode = document.querySelector("#stamped-reviews-tab");
  const config = { attributes: true, childList: true, subtree: true };
  const callback = (mutationList, observer) => {
    observer.disconnect();
    // Call these functions to handle re-written styles
    updateReviewAttributes();
    updateDateOfReview();
    observer.observe(targetNode, config);
  };
  const observer = new MutationObserver(callback);
  observer.observe(targetNode, config);
}

/**
 * Updates confirmation messages text displayed after review submission.
 */
function updateConfirmationMessage() {
  const confirmationMessages = document.querySelectorAll(
    ".stamped-thank-you p",
  );

  confirmationMessages.forEach((message, index) => {
    setElementProperties(message, {
      innerText:
        index === 0
          ? "Your review has been submitted!"
          : "Your review will be posted once it's been approved.",
    });
  });
}

/**
 * Updates the review counts, ratings on the review header, and adjusts fill in of stars based on the review data.
 */
function updateReviewCount() {
  const reviewCountProp = document.querySelector(
    ".stamped-header [itemprop=reviewCount]",
  );
  let reviewCount;
  if (reviewCountProp) {
    reviewCount = reviewCountProp.content;
  }
  const ratingValueProp = document.querySelector(
    ".stamped-header [itemprop=ratingValue]",
  );
  let ratingValue;
  if (ratingValueProp) {
    ratingValue = ratingValueProp.content;
  }
  if (
    document
      .querySelectorAll(".review-stars-header")
      .forEach((originalHeader) => {
        // Clone the original header to avoid direct modifications
        const header = originalHeader.cloneNode(true);

        if (reviewCount && reviewCount !== "" && reviewCount !== "0") {
          const roundedRating = parseFloat(ratingValue).toFixed(1);
          header.querySelector(".review-rating-text").innerText =
            `${roundedRating}`;
          header.classList.remove("none");
          header.querySelector(".review-rating-count").innerText =
            ` (${reviewCount} REVIEWS)`;
          const score = Math.round(ratingValue * 10);
          const scores = [
            score,
            score - 10,
            score - 20,
            score - 30,
            score - 40,
          ].map((val) => Math.max(0, Math.min(10, Math.floor(val))));

          header.querySelector(".review-title-text").textContent =
            `Users rated this product on average a ${score / 10} out of 5.`;
          header
            .querySelector(".review-star-1")
            .setAttribute("fill", `url(#Gradient${scores[0]})`);
          header
            .querySelector(".review-star-2")
            .setAttribute("fill", `url(#Gradient${scores[1]})`);
          header
            .querySelector(".review-star-3")
            .setAttribute("fill", `url(#Gradient${scores[2]})`);
          header
            .querySelector(".review-star-4")
            .setAttribute("fill", `url(#Gradient${scores[3]})`);
          header
            .querySelector(".review-star-5")
            .setAttribute("fill", `url(#Gradient${scores[4]})`);
        } else {
          header.classList.add("none");
        }

        // Replace the original header with clone
        originalHeader.parentNode.replaceChild(header, originalHeader);

        header.style.visibility = "visible";
      })
  );
  return { reviewCount, ratingValue };
}
window.updateReviewCount = updateReviewCount;
let initialStampedLoad = false;

/**
 * Executes all update functions to modify the reviews section.
 */
function executeAllUpdates() {
  updateSummary();
  updateCarousel();
  updatePhotoResolution();
  updateFormQuestions();
  updateFormFieldText();
  updateOptionalFieldText();
  createReviewGuidelines();
  styleAddPhotosButton();
  updateDateOfReview();
  updateReviewAttributes();
  updateFilters();
  updateFilterSelectText();
  updateReviewCount();
  updateConfirmationMessage();
  checkIfUpdatesNeeded();
}

/**
 * Execute updates once the 'stamped:reviews:loaded' event has fired.
 */
function initializeReviewsUpdate() {
  addEventListenerStamped(document, "stamped:reviews:loaded", () => {
    if (!initialStampedLoad) {
      executeAllUpdates();
      initialStampedLoad = true;
    }
  });
}

/**
 * Determine whether or not to run all updates.
 */
initializeReviewsUpdate();
